<template>
  <section class="validation-demo">
    <div class="validation-demo__container">
      <!-- Section Header -->
      <div class="validation-demo__header">
        <span class="validation-demo__badge">
          <i class="pi pi-bolt"></i>
          Prueba en Vivo
        </span>
        <h2 class="validation-demo__title">
          Valida cualquier email <span>ahora mismo</span>
        </h2>
        <p class="validation-demo__description">
          Ingresa un email y observa nuestro sistema de validación en acción. 
          Sin registro, sin tarjeta. 100% gratis.
        </p>
      </div>
      
      <!-- Validation Form -->
      <div class="validation-demo__form-wrapper">
        <div class="validation-demo__form">
          <div class="validation-demo__input-group">
            <i class="pi pi-envelope"></i>
            <InputText 
              v-model="email" 
              placeholder="ejemplo@tuempresa.com"
              class="validation-demo__input"
              :class="{ 'p-invalid': hasError }"
              @keyup.enter="validateEmail"
              :disabled="isLoading"
            />
            <Button 
              :label="isLoading ? 'Validando...' : 'Validar Ahora'"
              :icon="isLoading ? 'pi pi-spin pi-spinner' : 'pi pi-arrow-right'"
              iconPos="right"
              class="validation-demo__submit"
              @click="validateEmail"
              :disabled="isLoading || !email"
            />
          </div>
          <small v-if="hasError" class="validation-demo__error">
            {{ errorMessage }}
          </small>
        </div>
        
        <!-- Results Panel - Skeleton or Real Data -->
        <transition name="slide-fade">
          <div v-if="isLoading || result" class="validation-demo__results">
            <!-- Overall Status - Skeleton -->
            <div v-if="isLoading && !result" class="validation-demo__overall validation-demo__overall--skeleton">
              <div class="validation-demo__overall-icon skeleton-pulse">
                <i class="pi pi-spin pi-spinner"></i>
              </div>
              <div class="validation-demo__overall-content">
                <span class="validation-demo__overall-email">{{ email }}</span>
                <span class="validation-demo__overall-status validation-demo__status-loading">
                  <span class="loading-dots">Validando<span>.</span><span>.</span><span>.</span></span>
                </span>
              </div>
              <div class="validation-demo__confidence">
                <span class="validation-demo__confidence-value skeleton-text">--</span>
                <span class="validation-demo__confidence-label">Confianza</span>
              </div>
            </div>
            
            <!-- Overall Status - Real -->
            <div v-else class="validation-demo__overall" :class="[`validation-demo__overall--${result.overall}`, 'reveal-pop']">
              <div class="validation-demo__overall-icon">
                <i :class="getOverallIcon(result.overall)"></i>
              </div>
              <div class="validation-demo__overall-content">
                <span class="validation-demo__overall-email">{{ result.email }}</span>
                <span class="validation-demo__overall-status">{{ getOverallLabel(result.overall) }}</span>
              </div>
              <div class="validation-demo__confidence">
                <span class="validation-demo__confidence-value">{{ animatedConfidence }}%</span>
                <span class="validation-demo__confidence-label">Confianza</span>
              </div>
            </div>
            
            <!-- Validation Details Grid -->
            <div class="validation-demo__grid">
              <!-- DNS Check - Skeleton -->
              <div v-if="isLoading && !result" class="validation-demo__check validation-demo__check--skeleton reveal-section" style="--reveal-delay: 0.1s">
                <div class="validation-demo__check-header">
                  <i class="pi pi-server"></i>
                  <span>DNS / MX Records</span>
                  <Tag severity="secondary" value="Verificando..." />
                </div>
                <ul class="validation-demo__check-list">
                  <li class="skeleton-item"><span class="skeleton-bar"></span></li>
                  <li class="skeleton-item"><span class="skeleton-bar"></span></li>
                  <li class="skeleton-item"><span class="skeleton-bar"></span></li>
                </ul>
              </div>
              
              <!-- DNS Check - Real -->
              <div v-else class="validation-demo__check reveal-section" :class="`validation-demo__check--${result.dns.status}`" style="--reveal-delay: 0.1s">
                <div class="validation-demo__check-header">
                  <i class="pi pi-server"></i>
                  <span>DNS / MX Records</span>
                  <Tag :severity="getTagSeverity(result.dns.status)" :value="getStatusLabel(result.dns.status)" />
                </div>
                <ul class="validation-demo__check-list">
                  <li>
                    <i :class="result.dns.mx_records ? 'pi pi-check' : 'pi pi-times'"></i>
                    Registros MX
                  </li>
                  <li>
                    <i :class="result.dns.spf ? 'pi pi-check' : 'pi pi-times'"></i>
                    SPF configurado
                  </li>
                  <li>
                    <i :class="result.dns.dmarc ? 'pi pi-check' : 'pi pi-times'"></i>
                    DMARC configurado
                  </li>
                </ul>
              </div>
              
              <!-- SMTP Check - Skeleton -->
              <div v-if="isLoading && !result" class="validation-demo__check validation-demo__check--skeleton reveal-section" style="--reveal-delay: 0.2s">
                <div class="validation-demo__check-header">
                  <i class="pi pi-envelope"></i>
                  <span>Verificación SMTP</span>
                  <Tag severity="secondary">
                    <i class="pi pi-spin pi-spinner" style="font-size: 0.75rem; margin-right: 4px;"></i>
                    Conectando...
                  </Tag>
                </div>
                <ul class="validation-demo__check-list">
                  <li class="skeleton-item"><span class="skeleton-bar"></span></li>
                  <li class="skeleton-item"><span class="skeleton-bar" style="width: 80%;"></span></li>
                </ul>
                <p class="validation-demo__loading-hint">
                  <i class="pi pi-info-circle"></i>
                  Conectando con el servidor de correo...
                </p>
              </div>
              
              <!-- SMTP Check - Real -->
              <div v-else class="validation-demo__check reveal-section" :class="`validation-demo__check--${result.smtp.status}`" style="--reveal-delay: 0.2s">
                <div class="validation-demo__check-header">
                  <i class="pi pi-envelope"></i>
                  <span>Verificación SMTP</span>
                  <Tag :severity="getTagSeverity(result.smtp.status)" :value="getStatusLabel(result.smtp.status)" />
                </div>
                <ul class="validation-demo__check-list">
                  <li>
                    <i :class="result.smtp.mailbox_exists ? 'pi pi-check' : 'pi pi-times'"></i>
                    {{ result.smtp.mailbox_exists ? 'Buzón existe' : 'Buzón no encontrado' }}
                  </li>
                  <li class="validation-demo__check-response">
                    <i class="pi pi-info-circle"></i>
                    {{ truncateResponse(result.smtp.response) }}
                  </li>
                </ul>
              </div>
              
              <!-- Risk Indicators - Skeleton -->
              <div v-if="isLoading && !result" class="validation-demo__check validation-demo__check--skeleton validation-demo__check--risks reveal-section" style="--reveal-delay: 0.3s">
                <div class="validation-demo__check-header">
                  <i class="pi pi-shield"></i>
                  <span>Indicadores de Riesgo</span>
                </div>
                <ul class="validation-demo__check-list validation-demo__risks">
                  <li class="skeleton-item"><span class="skeleton-bar" style="width: 60%;"></span></li>
                  <li class="skeleton-item"><span class="skeleton-bar" style="width: 70%;"></span></li>
                  <li class="skeleton-item"><span class="skeleton-bar" style="width: 65%;"></span></li>
                </ul>
              </div>
              
              <!-- Risk Indicators - Real -->
              <div v-else class="validation-demo__check validation-demo__check--risks reveal-section" style="--reveal-delay: 0.3s">
                <div class="validation-demo__check-header">
                  <i class="pi pi-shield"></i>
                  <span>Indicadores de Riesgo</span>
                </div>
                <ul class="validation-demo__check-list validation-demo__risks">
                  <li :class="{ 'is-warning': result.catchall.is_catchall }">
                    <i :class="result.catchall.is_catchall ? 'pi pi-exclamation-triangle' : 'pi pi-check-circle'"></i>
                    <span>Catch-all</span>
                    <Tag 
                      :severity="result.catchall.is_catchall ? 'warn' : 'success'" 
                      :value="result.catchall.is_catchall ? 'Sí' : 'No'" 
                      class="validation-demo__risk-tag"
                    />
                  </li>
                  <li :class="{ 'is-danger': result.disposable.is_disposable }">
                    <i :class="result.disposable.is_disposable ? 'pi pi-ban' : 'pi pi-check-circle'"></i>
                    <span>Desechable</span>
                    <Tag 
                      :severity="result.disposable.is_disposable ? 'danger' : 'success'" 
                      :value="result.disposable.is_disposable ? 'Sí' : 'No'" 
                      class="validation-demo__risk-tag"
                    />
                  </li>
                  <li :class="{ 'is-warning': result.role.is_role }">
                    <i :class="result.role.is_role ? 'pi pi-exclamation-triangle' : 'pi pi-check-circle'"></i>
                    <span>Role-based</span>
                    <Tag 
                      :severity="result.role.is_role ? 'warn' : 'success'" 
                      :value="result.role.is_role ? 'Sí' : 'No'" 
                      class="validation-demo__risk-tag"
                    />
                  </li>
                </ul>
              </div>
              
              <!-- Syntax Check - Skeleton -->
              <div v-if="isLoading && !result" class="validation-demo__check validation-demo__check--skeleton reveal-section" style="--reveal-delay: 0.15s">
                <div class="validation-demo__check-header">
                  <i class="pi pi-code"></i>
                  <span>Sintaxis</span>
                  <Tag severity="success" value="Válida" />
                </div>
                <p class="validation-demo__check-note skeleton-text-inline">
                  Verificando formato...
                </p>
              </div>
              
              <!-- Syntax Check - Real -->
              <div v-else class="validation-demo__check reveal-section" :class="`validation-demo__check--${result.syntax.status}`" style="--reveal-delay: 0.15s">
                <div class="validation-demo__check-header">
                  <i class="pi pi-code"></i>
                  <span>Sintaxis</span>
                  <Tag :severity="getTagSeverity(result.syntax.status)" :value="result.syntax.valid ? 'Válida' : 'Inválida'" />
                </div>
                <p class="validation-demo__check-note">
                  Validado el {{ formatDate(result.validated_at) }}
                </p>
              </div>
            </div>
            
            <!-- CTA - Only show when complete -->
            <div v-if="result" class="validation-demo__cta reveal-section" style="--reveal-delay: 0.4s">
              <p>¿Te gustó? Valida miles de emails en segundos.</p>
              <Button 
                label="Crear Cuenta Gratis" 
                icon="pi pi-user-plus"
                class="p-button-lg validation-demo__cta-button"
              />
            </div>
          </div>
        </transition>
        
        <!-- Remaining validations indicator -->
        <div v-if="!isRegistered && validationCount > 0" class="validation-demo__remaining">
          <i class="pi pi-info-circle"></i>
          <span>Te quedan <strong>{{ remainingFree }}</strong> validaciones gratuitas</span>
        </div>
      </div>
    </div>
    
    <!-- Email Gate Modal -->
    <Dialog 
      v-model:visible="showEmailGate" 
      modal 
      :closable="true"
      :draggable="false"
      class="validation-demo__gate-dialog"
      :style="{ width: '90%', maxWidth: '450px' }"
      :pt="{
        root: { style: 'background: white; border-radius: 12px; box-shadow: 0 25px 50px -12px rgba(0,0,0,0.25);' },
        header: { style: 'background: white; border-bottom: 1px solid #e5e7eb; padding: 1.25rem;' },
        content: { style: 'background: white; padding: 1.25rem;' },
        footer: { style: 'background: white; border-top: 1px solid #e5e7eb; padding: 1.25rem;' },
        mask: { style: 'background: rgba(0,0,0,0.5);' }
      }"
    >
      <template #header>
        <div class="validation-demo__gate-header">
          <i class="pi pi-lock"></i>
          <span>Desbloquea más validaciones</span>
        </div>
      </template>
      
      <div class="validation-demo__gate-content">
        <p class="validation-demo__gate-message">
          ¡Genial! Ya usaste tus <strong>{{ FREE_VALIDATIONS_LIMIT }} validaciones gratuitas</strong>. 
          Ingresa tu email para continuar validando sin límites.
        </p>
        
        <div class="validation-demo__gate-form">
          <div class="p-inputgroup">
            <span class="p-inputgroup-addon">
              <i class="pi pi-envelope"></i>
            </span>
            <InputText 
              v-model="leadEmail" 
              placeholder="tu@email.com"
              :class="{ 'p-invalid': leadEmailError }"
              @keyup.enter="registerLeadEmail"
            />
          </div>
          <small v-if="leadEmailError" class="p-error">{{ leadEmailError }}</small>
        </div>
        
        <div class="validation-demo__gate-benefits">
          <h4>¿Por qué pedimos tu email?</h4>
          <ul>
            <li><i class="pi pi-check"></i> Validaciones ilimitadas en el demo</li>
            <li><i class="pi pi-check"></i> Recibirás tips para mejorar tu deliverability</li>
            <li><i class="pi pi-check"></i> Acceso anticipado a nuevas funciones</li>
          </ul>
        </div>
      </div>
      
      <template #footer>
        <Button 
          label="Continuar Validando" 
          icon="pi pi-arrow-right" 
          iconPos="right"
          class="p-button-action w-full"
          @click="registerLeadEmail"
        />
      </template>
    </Dialog>
  </section>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import Tag from 'primevue/tag';
import Dialog from 'primevue/dialog';

const email = ref('');
const isLoading = ref(false);
const hasError = ref(false);
const animatedConfidence = ref(0);
const errorMessage = ref('');
const result = ref(null);

// Email gate state
const FREE_VALIDATIONS_LIMIT = 3;
const showEmailGate = ref(false);
const leadEmail = ref('');
const leadEmailError = ref('');
const validationCount = ref(0);
const remainingFree = ref(FREE_VALIDATIONS_LIMIT);
const isRegistered = ref(false);
const pendingEmailToValidate = ref('');

// Load state from localStorage on mount
onMounted(() => {
  const storedLeadEmail = localStorage.getItem('demo_lead_email');
  const storedCount = parseInt(localStorage.getItem('demo_validation_count') || '0', 10);
  
  if (storedLeadEmail) {
    leadEmail.value = storedLeadEmail;
    isRegistered.value = true;
  }
  
  validationCount.value = storedCount;
  remainingFree.value = isRegistered.value ? 999 : Math.max(FREE_VALIDATIONS_LIMIT - storedCount, 0);
});

// Register lead email
const registerLeadEmail = async () => {
  const emailValue = leadEmail.value.trim().toLowerCase();
  
  // Validate email format
  if (!emailValue.match(/^[^@\s]+@[^@\s]+\.[^@\s]+$/)) {
    leadEmailError.value = 'Por favor ingresa un email válido';
    return;
  }
  
  leadEmailError.value = '';
  
  // Save to localStorage
  localStorage.setItem('demo_lead_email', emailValue);
  isRegistered.value = true;
  remainingFree.value = 999;
  showEmailGate.value = false;
  
  // If there was a pending validation, continue it
  if (pendingEmailToValidate.value) {
    const emailToValidate = pendingEmailToValidate.value;
    pendingEmailToValidate.value = '';
    email.value = emailToValidate;
    await validateEmail();
  }
};

const validateEmail = async () => {
  if (!email.value) return;
  
  // Check if email gate should trigger
  if (!isRegistered.value && validationCount.value >= FREE_VALIDATIONS_LIMIT) {
    pendingEmailToValidate.value = email.value;
    showEmailGate.value = true;
    return;
  }
  
  hasError.value = false;
  errorMessage.value = '';
  isLoading.value = true;
  result.value = null;
  animatedConfidence.value = 0; // Reset confidence animation
  
  try {
    // Get CSRF token for Rails
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
    
    // Build request body
    const requestBody = { 
      email: email.value.trim().toLowerCase(),
      _ts: Date.now() // Timestamp for bot protection
    };
    
    // Include lead email if registered
    if (isRegistered.value && leadEmail.value) {
      requestBody.lead_email = leadEmail.value;
    }
    
    const response = await fetch('/api/v1/public/validate', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken || '',
      },
      body: JSON.stringify(requestBody)
    });
    
    const data = await response.json();
    
    // Handle email gate response
    if (response.status === 402 && data.error === 'email_gate') {
      pendingEmailToValidate.value = email.value;
      showEmailGate.value = true;
      return;
    }
    
    if (!response.ok) {
      throw new Error(data.error || 'Error al validar el email');
    }
    
    result.value = data;
    
    // Animate confidence score from 0 to final value
    animateConfidenceScore(data.confidence);
    
    // Update validation count
    validationCount.value++;
    localStorage.setItem('demo_validation_count', validationCount.value.toString());
    remainingFree.value = data.remaining_free ?? Math.max(FREE_VALIDATIONS_LIMIT - validationCount.value, 0);
    
  } catch (error) {
    hasError.value = true;
    errorMessage.value = error.message || 'Error de conexión. Intenta de nuevo.';
  } finally {
    isLoading.value = false;
  }
};

const getOverallIcon = (status) => {
  const icons = {
    valid: 'pi pi-check-circle',
    invalid: 'pi pi-times-circle',
    risky: 'pi pi-exclamation-triangle',
    unknown: 'pi pi-question-circle'
  };
  return icons[status] || icons.unknown;
};

const getOverallLabel = (status) => {
  const labels = {
    valid: 'Email Válido y Entregable',
    invalid: 'Email Inválido',
    risky: 'Email con Riesgo',
    unknown: 'No se pudo verificar'
  };
  return labels[status] || labels.unknown;
};

// Animate confidence score from 0 to target value
const animateConfidenceScore = (targetValue) => {
  const duration = 1000; // 1 second
  const startTime = performance.now();
  const startValue = 0;
  
  const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);
  
  const animate = (currentTime) => {
    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const easedProgress = easeOutCubic(progress);
    
    animatedConfidence.value = Math.round(startValue + (targetValue - startValue) * easedProgress);
    
    if (progress < 1) {
      requestAnimationFrame(animate);
    }
  };
  
  requestAnimationFrame(animate);
};

const getTagSeverity = (status) => {
  const severities = {
    valid: 'success',
    success: 'success',
    ok: 'success',
    invalid: 'danger',
    danger: 'danger',
    failed: 'danger',
    warning: 'warn',
    risky: 'warn',
    unknown: 'secondary'
  };
  return severities[status] || 'secondary';
};

const getStatusLabel = (status) => {
  const labels = {
    valid: 'OK',
    success: 'OK',
    ok: 'OK',
    invalid: 'Fallo',
    failed: 'Fallo',
    danger: 'Error',
    warning: 'Alerta',
    unknown: 'Desconocido'
  };
  return labels[status] || status;
};

const truncateResponse = (response) => {
  if (!response) return 'Sin respuesta';
  return response.length > 60 ? response.substring(0, 60) + '...' : response;
};

const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleString('es-ES', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};
</script>

<style lang="scss" scoped>
@import '@/design-system/variables';

// ===========================================
// VALIDATION DEMO SECTION
// ===========================================

.validation-demo {
  padding: $tl-section-spacing 0;
  background: linear-gradient(180deg, $tl-white 0%, $tl-gray-50 100%);
}

.validation-demo__container {
  max-width: 900px;
  margin: 0 auto;
  padding: 0 $tl-page-padding;
  
  @media (max-width: 768px) {
    padding: 0 $tl-space-4;
  }
}

// Section Header
.validation-demo__header {
  text-align: center;
  margin-bottom: $tl-space-10;
}

.validation-demo__badge {
  display: inline-flex;
  align-items: center;
  gap: $tl-space-2;
  background: linear-gradient(90deg, $tl-primary, $tl-action);
  color: $tl-white;
  padding: $tl-space-2 $tl-space-4;
  border-radius: $tl-radius-full;
  font-size: $tl-font-size-sm;
  font-weight: $tl-font-weight-semibold;
  margin-bottom: $tl-space-4;
}

.validation-demo__title {
  font-size: clamp(1.75rem, 4vw, 2.25rem);
  font-weight: $tl-font-weight-bold;
  color: $tl-jet;
  margin-bottom: $tl-space-4;
  
  span {
    color: $tl-primary;
  }
}

.validation-demo__description {
  font-size: $tl-font-size-lg;
  color: $tl-gray-600;
  max-width: 600px;
  margin: 0 auto;
}

// Form
.validation-demo__form-wrapper {
  background: $tl-white;
  border-radius: $tl-radius-xl;
  box-shadow: $tl-shadow-xl;
  overflow: hidden;
}

.validation-demo__form {
  padding: $tl-space-8;
  border-bottom: 1px solid $tl-border-color-light;
  
  @media (max-width: 768px) {
    padding: $tl-space-6;
  }
}

.validation-demo__input-group {
  display: flex;
  align-items: center;
  gap: $tl-space-3;
  background: $tl-gray-50;
  border: 2px solid $tl-border-color;
  border-radius: $tl-radius-lg;
  padding: $tl-space-2 $tl-space-3;
  transition: $tl-transition-default;
  
  &:focus-within {
    border-color: $tl-primary;
    box-shadow: $tl-shadow-focus;
  }
  
  > i {
    color: $tl-gray-400;
    font-size: $tl-font-size-xl;
  }
  
  @media (max-width: 600px) {
    flex-wrap: wrap;
    
    > i {
      display: none;
    }
  }
}

.validation-demo__input {
  flex: 1;
  border: none !important;
  background: transparent !important;
  box-shadow: none !important;
  font-size: $tl-font-size-lg;
  padding: $tl-space-3 0;
  
  &::placeholder {
    color: $tl-gray-400;
  }
  
  @media (max-width: 600px) {
    width: 100%;
    flex: none;
  }
}

.validation-demo__submit {
  background: linear-gradient(90deg, $tl-primary, $tl-action) !important;
  border: none !important;
  font-weight: $tl-font-weight-semibold;
  white-space: nowrap;
  
  &:hover:not(:disabled) {
    box-shadow: $tl-shadow-action-glow;
  }
  
  @media (max-width: 600px) {
    width: 100%;
    margin-top: $tl-space-2;
  }
}

.validation-demo__error {
  display: block;
  color: $tl-error;
  margin-top: $tl-space-2;
  font-size: $tl-font-size-sm;
}

// Results
.validation-demo__results {
  padding: $tl-space-8;
  
  @media (max-width: 768px) {
    padding: $tl-space-6;
  }
}

// Overall Status
.validation-demo__overall {
  display: flex;
  align-items: center;
  gap: $tl-space-4;
  padding: $tl-space-5;
  border-radius: $tl-radius-lg;
  margin-bottom: $tl-space-6;
  
  &--valid {
    background: $tl-success-light;
    
    .validation-demo__overall-icon {
      background: $tl-success;
    }
  }
  
  &--invalid {
    background: $tl-error-light;
    
    .validation-demo__overall-icon {
      background: $tl-error;
    }
  }
  
  &--risky {
    background: $tl-warning-light;
    
    .validation-demo__overall-icon {
      background: $tl-warning;
    }
  }
  
  &--unknown {
    background: $tl-gray-100;
    
    .validation-demo__overall-icon {
      background: $tl-gray-500;
    }
  }
  
  @media (max-width: 600px) {
    flex-wrap: wrap;
  }
}

.validation-demo__overall-icon {
  width: 48px;
  height: 48px;
  border-radius: $tl-radius-full;
  display: flex;
  align-items: center;
  justify-content: center;
  color: $tl-white;
  
  i {
    font-size: $tl-font-size-2xl;
  }
}

.validation-demo__overall-content {
  flex: 1;
  
  @media (max-width: 600px) {
    flex: none;
    width: calc(100% - 64px);
  }
}

.validation-demo__overall-email {
  display: block;
  font-size: $tl-font-size-lg;
  font-weight: $tl-font-weight-semibold;
  color: $tl-jet;
  word-break: break-all;
}

.validation-demo__overall-status {
  font-size: $tl-font-size-sm;
  color: $tl-gray-600;
}

.validation-demo__confidence {
  text-align: center;
  
  @media (max-width: 600px) {
    width: 100%;
    margin-top: $tl-space-3;
    padding-top: $tl-space-3;
    border-top: 1px solid rgba(0, 0, 0, 0.1);
    display: flex;
    align-items: center;
    justify-content: center;
    gap: $tl-space-2;
  }
}

.validation-demo__confidence-value {
  display: block;
  font-size: $tl-font-size-2xl;
  font-weight: $tl-font-weight-bold;
  color: $tl-jet;
  
  @media (max-width: 600px) {
    font-size: $tl-font-size-xl;
  }
}

.validation-demo__confidence-label {
  font-size: $tl-font-size-xs;
  color: $tl-gray-500;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

// Grid
.validation-demo__grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: $tl-space-4;
  margin-bottom: $tl-space-6;
  
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
}

// Check Card
.validation-demo__check {
  background: $tl-gray-50;
  border-radius: $tl-radius-md;
  padding: $tl-space-4;
  border-left: 4px solid $tl-gray-300;
  
  &--valid, &--success, &--ok {
    border-left-color: $tl-success;
  }
  
  &--invalid, &--failed, &--danger {
    border-left-color: $tl-error;
  }
  
  &--warning, &--risky {
    border-left-color: $tl-warning;
  }
}

.validation-demo__check-header {
  display: flex;
  align-items: center;
  gap: $tl-space-2;
  margin-bottom: $tl-space-3;
  
  > i {
    color: $tl-gray-600;
  }
  
  > span {
    flex: 1;
    font-weight: $tl-font-weight-semibold;
    font-size: $tl-font-size-sm;
    color: $tl-jet;
  }
}

.validation-demo__check-list {
  list-style: none;
  padding: 0;
  margin: 0;
  
  li {
    display: flex;
    align-items: center;
    gap: $tl-space-2;
    font-size: $tl-font-size-sm;
    color: $tl-gray-700;
    padding: $tl-space-1 0;
    
    i.pi-check {
      color: $tl-success;
    }
    
    i.pi-times {
      color: $tl-error;
    }
    
    i.pi-info-circle {
      color: $tl-gray-400;
    }
  }
}

.validation-demo__check-response {
  font-size: $tl-font-size-xs !important;
  color: $tl-gray-500 !important;
  font-style: italic;
}

.validation-demo__check-note {
  font-size: $tl-font-size-xs;
  color: $tl-gray-500;
  margin: $tl-space-2 0 0;
}

// Risks
.validation-demo__risks {
  li {
    justify-content: space-between;
    
    span {
      flex: 1;
    }
    
    &.is-warning i {
      color: $tl-warning;
    }
    
    &.is-danger i {
      color: $tl-error;
    }
  }
}

.validation-demo__risk-tag {
  font-size: $tl-font-size-xs;
}

// CTA
.validation-demo__cta {
  text-align: center;
  padding-top: $tl-space-6;
  border-top: 1px solid $tl-border-color-light;
  
  p {
    font-size: $tl-font-size-base;
    color: $tl-gray-600;
    margin-bottom: $tl-space-4;
  }
}

.validation-demo__cta-button {
  background: $tl-jet !important;
  border-color: $tl-jet !important;
  
  &:hover {
    background: lighten($tl-jet, 10%) !important;
  }
}

// Transition
.slide-fade-enter-active {
  transition: all 0.3s ease-out;
}

.slide-fade-leave-active {
  transition: all 0.2s ease-in;
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  transform: translateY(-10px);
  opacity: 0;
}

// ===========================================
// REMAINING VALIDATIONS INDICATOR
// ===========================================

.validation-demo__remaining {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: $tl-space-2;
  margin-top: $tl-space-4;
  padding: $tl-space-3 $tl-space-4;
  background: $tl-gray-100;
  border-radius: $tl-radius-md;
  font-size: $tl-font-size-sm;
  color: $tl-gray-600;
  
  i {
    color: $tl-action;
  }
  
  strong {
    color: $tl-action;
    font-weight: $tl-font-weight-semibold;
  }
}

// ===========================================
// EMAIL GATE MODAL
// ===========================================

:deep(.validation-demo__gate-dialog) {
  .p-dialog-content {
    background: $tl-white;
    padding: $tl-space-5;
  }
  
  .p-dialog-header {
    background: $tl-white;
    border-bottom: 1px solid $tl-border-color-light;
    padding: $tl-space-4 $tl-space-5;
  }
  
  .p-dialog-footer {
    background: $tl-white;
    border-top: 1px solid $tl-border-color-light;
    padding: $tl-space-4 $tl-space-5;
  }
}

.validation-demo__gate-header {
  display: flex;
  align-items: center;
  gap: $tl-space-3;
  font-size: $tl-font-size-lg;
  font-weight: $tl-font-weight-semibold;
  color: $tl-gray-900;
  
  i {
    color: $tl-action;
    font-size: 1.25rem;
  }
}

.validation-demo__gate-content {
  padding: $tl-space-4 0;
}

.validation-demo__gate-message {
  font-size: $tl-font-size-base;
  color: $tl-gray-600;
  line-height: 1.6;
  margin-bottom: $tl-space-5;
  
  strong {
    color: $tl-gray-900;
  }
}

.validation-demo__gate-form {
  margin-bottom: $tl-space-5;
  
  .p-inputgroup {
    width: 100%;
  }
  
  .p-inputgroup-addon {
    background: $tl-gray-100;
    border-color: $tl-border-color;
    color: $tl-gray-500;
  }
  
  .p-inputtext {
    width: 100%;
    
    &:focus {
      border-color: $tl-action;
      box-shadow: 0 0 0 2px rgba($tl-action, 0.1);
    }
    
    &.p-invalid {
      border-color: $tl-error;
    }
  }
  
  .p-error {
    display: block;
    margin-top: $tl-space-2;
    color: $tl-error;
    font-size: $tl-font-size-sm;
  }
}

.validation-demo__gate-benefits {
  background: $tl-gray-50;
  border-radius: $tl-radius-md;
  padding: $tl-space-4;
  
  h4 {
    font-size: $tl-font-size-sm;
    font-weight: $tl-font-weight-semibold;
    color: $tl-gray-700;
    margin-bottom: $tl-space-3;
  }
  
  ul {
    list-style: none;
    padding: 0;
    margin: 0;
    
    li {
      display: flex;
      align-items: center;
      gap: $tl-space-2;
      font-size: $tl-font-size-sm;
      color: $tl-gray-600;
      padding: $tl-space-1 0;
      
      i {
        color: $tl-success;
        font-size: 0.75rem;
      }
    }
  }
}

// ===========================================
// SKELETON LOADING STATES
// ===========================================

// Skeleton pulse animation
@keyframes skeletonPulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

@keyframes loadingDots {
  0%, 20% {
    opacity: 0;
  }
  40% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
}

// Reveal animation for sections
@keyframes revealPop {
  0% {
    opacity: 0;
    transform: scale(0.95) translateY(10px);
  }
  100% {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.reveal-pop {
  animation: revealPop 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}

.reveal-section {
  animation: revealPop 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards;
  animation-delay: var(--reveal-delay, 0s);
}

.skeleton-pulse {
  animation: skeletonPulse 1.5s ease-in-out infinite;
}

// Skeleton overall status
.validation-demo__overall--skeleton {
  background: $tl-gray-100;
  
  .validation-demo__overall-icon {
    background: $tl-gray-300;
    animation: skeletonPulse 1.5s ease-in-out infinite;
    
    i {
      color: $tl-white;
    }
  }
}

.validation-demo__status-loading {
  color: $tl-gray-500;
}

.loading-dots {
  span {
    animation: loadingDots 1.4s infinite;
    
    &:nth-child(1) { animation-delay: 0s; }
    &:nth-child(2) { animation-delay: 0.2s; }
    &:nth-child(3) { animation-delay: 0.4s; }
  }
}

.skeleton-text {
  color: $tl-gray-400;
  animation: skeletonPulse 1.5s ease-in-out infinite;
}

// Skeleton check cards
.validation-demo__check--skeleton {
  background: $tl-gray-50;
  border-color: $tl-gray-200;
  
  .validation-demo__check-header {
    i {
      color: $tl-gray-400;
    }
  }
}

.skeleton-item {
  display: flex;
  align-items: center;
  height: 20px;
}

.skeleton-bar {
  display: inline-block;
  height: 12px;
  width: 100%;
  background: linear-gradient(
    90deg,
    $tl-gray-200 25%,
    $tl-gray-100 50%,
    $tl-gray-200 75%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s ease-in-out infinite;
  border-radius: $tl-radius-sm;
}

.skeleton-text-inline {
  color: $tl-gray-400;
  font-style: italic;
}

// Loading hint for SMTP
.validation-demo__loading-hint {
  display: flex;
  align-items: center;
  gap: $tl-space-2;
  font-size: $tl-font-size-sm;
  color: $tl-gray-500;
  margin-top: $tl-space-2;
  padding-top: $tl-space-2;
  border-top: 1px dashed $tl-gray-200;
  
  i {
    color: $tl-primary;
    animation: skeletonPulse 1s ease-in-out infinite;
  }
}
</style>
